import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface AuthProps {
  onAuthSuccess: () => void;
}

export function Auth({ onAuthSuccess }: AuthProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [isMagicLinkMode, setIsMagicLinkMode] = useState(false);

  useEffect(() => {
    // Handle magic link and password reset callbacks
    const handleAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const error = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');

      if (error || errorDescription) {
        setError(errorDescription || 'Authentication failed. Please try again.');
        return;
      }

      if (accessToken && refreshToken) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) throw error;
          if (data.session) {
            // Clear hash from URL without triggering a reload
            window.history.replaceState(null, '', window.location.pathname);
            onAuthSuccess();
          }
        } catch (err) {
          console.error('Error setting session:', err);
          setError('Failed to authenticate. Please try again.');
        }
      }
    };

    handleAuthCallback();
  }, [onAuthSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (isResetMode) {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (resetError) throw resetError;
        setSuccess('Check your email for password reset instructions.');
      } else if (isMagicLinkMode) {
        const { error: magicLinkError } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: window.location.origin,
          }
        });
        if (magicLinkError) throw magicLinkError;
        setSuccess('Check your email for the magic link.');
      } else {
        const { error: authError } = await supabase.auth.signInWithPassword({ 
          email, 
          password 
        });
        if (authError) throw authError;
        onAuthSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;
      setSuccess('Password updated successfully! You can now sign in with your new password.');
      
      // Clear hash from URL without triggering a reload
      window.history.replaceState(null, '', window.location.pathname);
      
      // Reset form
      setPassword('');
      setIsResetMode(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const isPasswordUpdateFlow = window.location.hash.includes('#access_token=');

  const resetForm = () => {
    setIsResetMode(false);
    setIsMagicLinkMode(false);
    setEmail('');
    setPassword('');
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {/* Logo and Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 mb-3">
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* QR Code Style Icon */}
              <rect x="4" y="4" width="24" height="24" rx="2" fill="#2563EB"/>
              <rect x="36" y="4" width="24" height="24" rx="2" fill="#2563EB"/>
              <rect x="4" y="36" width="24" height="24" rx="2" fill="#2563EB"/>

              {/* Inner squares for QR pattern */}
              <rect x="10" y="10" width="12" height="12" rx="1" fill="white"/>
              <rect x="42" y="10" width="12" height="12" rx="1" fill="white"/>
              <rect x="10" y="42" width="12" height="12" rx="1" fill="white"/>

              {/* Connection dots */}
              <circle cx="32" cy="32" r="3" fill="#2563EB"/>
              <circle cx="32" cy="16" r="2" fill="#2563EB"/>
              <circle cx="16" cy="32" r="2" fill="#2563EB"/>
              <circle cx="48" cy="32" r="2" fill="#2563EB"/>
              <circle cx="32" cy="48" r="2" fill="#2563EB"/>

              {/* Small accent dots */}
              <rect x="36" y="36" width="4" height="4" rx="1" fill="#2563EB"/>
              <rect x="44" y="36" width="4" height="4" rx="1" fill="#2563EB"/>
              <rect x="52" y="36" width="4" height="4" rx="1" fill="#2563EB"/>
              <rect x="36" y="44" width="4" height="4" rx="1" fill="#2563EB"/>
              <rect x="36" y="52" width="4" height="4" rx="1" fill="#2563EB"/>
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">NetworkQR</h1>
          <p className="text-sm text-gray-500">Professional QR Code Management</p>
        </div>

        <h2 className="text-xl font-semibold text-center mb-6 text-gray-700">
          {isPasswordUpdateFlow ? 'Update Password' :
           isResetMode ? 'Reset Password' :
           isMagicLinkMode ? 'Magic Link Sign In' : 'Sign In'}
        </h2>
        
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-600 p-3 rounded-md mb-4">
            {success}
          </div>
        )}

        <form onSubmit={isPasswordUpdateFlow ? handleUpdatePassword : handleSubmit} className="space-y-4">
          {!isPasswordUpdateFlow && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                required
              />
            </div>
          )}

          {!isResetMode && !isMagicLinkMode && (
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {isPasswordUpdateFlow ? 'New Password' : 'Password'}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                required={!isMagicLinkMode}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 
             isPasswordUpdateFlow ? 'Update Password' : 
             isResetMode ? 'Send Reset Instructions' :
             isMagicLinkMode ? 'Send Magic Link' : 'Sign In'}
          </button>
        </form>

        {!isPasswordUpdateFlow && !success && (
          <div className="mt-4 text-center space-y-2">
            {!isResetMode && !isMagicLinkMode ? (
              <>
                <button
                  onClick={() => {
                    resetForm();
                    setIsMagicLinkMode(true);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 block w-full"
                >
                  Sign in with Magic Link
                </button>
                <button
                  onClick={() => {
                    resetForm();
                    setIsResetMode(true);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 block w-full"
                >
                  Forgot Password?
                </button>
                <a
                  href="https://www.danny.support/networkqr"
                  className="text-sm text-blue-600 hover:text-blue-700 block"
                >
                  Don't have an account? Sign Up
                </a>
              </>
            ) : (
              <button
                onClick={resetForm}
                className="text-sm text-blue-600 hover:text-blue-700 block w-full"
              >
                Back to Sign In
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}